import React from 'react';

// TODO: Rebranding-Übergang — altes Billution-Logo nach Abschluss entfernen

interface BrandLogoProps {
  /** Tailwind height classes applied to the FaeBill logo (primary brand), e.g. "h-8 sm:h-10" */
  className?: string;
  /** Optional extra classes for the outer wrapper div (only used in 'lockup' mode) */
  wrapperClassName?: string;
  /**
   * Rendering mode:
   * - 'lockup' (default): FaeBill + divider + legacy Billution logo side-by-side in a flex wrapper.
   * - 'split-center': Returns a React Fragment — FaeBill logo in normal flow (lands left in the
   *   caller's flex container) plus the legacy logo absolutely centred relative to the nearest
   *   `relative`-positioned ancestor (i.e. the header flex container).
   *   The caller's header container must have `position: relative` (Tailwind: `relative`).
   * - 'solo': Renders only the FaeBill logo — no divider, no legacy Billution logo.
   *   Use when the legacy logo is placed separately in the layout (e.g. next to the nav).
   */
  mode?: 'lockup' | 'split-center' | 'solo';
}

/**
 * Inline SVG wordmark for FaeBill. Renders the brand's line-mark icon + "FaeBill" text
 * using DM Sans (loaded globally). Avoids <img> so DM Sans is always available.
 *
 * @param className - Tailwind height classes; `w-auto` is always added internally.
 */
function FaeBillWordmark({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 200 52"
      className={`${className} w-auto text-primary`}
      role="img"
      aria-label="FaeBill"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Mint-Markenlinie bleibt in beiden Themes; die restliche Wortmarke folgt
          `currentColor` (== --text-primary via `text-primary`): Weiss im Dark,
          Navy-Tinte im Light. Opazitaeten identisch zur bisherigen Optik. */}
      <line x1="2" y1="18" x2="26" y2="18" stroke="#20D08A" strokeWidth="2.8" strokeLinecap="round" />
      <line x1="2" y1="26" x2="20" y2="26" stroke="currentColor" strokeOpacity={0.6} strokeWidth="2.2" strokeLinecap="round" />
      <line x1="2" y1="34" x2="13" y2="34" stroke="currentColor" strokeOpacity={0.36} strokeWidth="2.2" strokeLinecap="round" />
      <text y="32" fontFamily="'DM Sans','Helvetica Neue',Helvetica,Arial,sans-serif" fontSize="27" letterSpacing="-0.5">
        <tspan x="42" fontWeight="300" fill="currentColor" fillOpacity={0.78}>Fae</tspan>
        <tspan fontWeight="700" fill="currentColor">Bill</tspan>
      </text>
    </svg>
  );
}

/**
 * Transitional brand logo component.
 *
 * In `lockup` mode (default) renders the new FaeBill logo alongside the legacy
 * Billution logo separated by a vertical divider — unchanged from the original design.
 *
 * In `split-center` mode renders a Fragment: FaeBill stays in normal document flow
 * while the legacy logo is absolutely positioned in the horizontal/vertical centre of
 * the nearest `relative` ancestor (the header bar). Use `hidden md:block` to suppress
 * the legacy logo on mobile where it would overlap the hamburger menu.
 */
export function BrandLogo({ className = 'h-8 sm:h-10', wrapperClassName, mode = 'lockup' }: BrandLogoProps) {
  if (mode === 'solo') {
    return <FaeBillWordmark className={className} />;
  }

  if (mode === 'split-center') {
    return (
      <>
        <FaeBillWordmark className={className} />
        {/* Responsive-Sichtbarkeit (hidden md:block) am Wrapper, Theme-Toggle
            (.fb-legacy-*) an den einzelnen <img> — getrennte Elemente, damit sich
            die display-Regeln nicht gegenseitig ueberschreiben (§7). */}
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block pointer-events-none">
          <img
            src="/billution-logo-reversed.svg"
            alt="vormals Billution"
            className="fb-legacy-dark h-5 sm:h-6 opacity-70"
          />
          <img
            src="/billution-logo.svg"
            alt=""
            aria-hidden="true"
            className="fb-legacy-light h-5 sm:h-6 opacity-70"
          />
        </span>
      </>
    );
  }

  // lockup (default)
  return (
    <div className={`flex items-center${wrapperClassName ? ` ${wrapperClassName}` : ''}`}>
      <FaeBillWordmark className={className} />
      <span
        className="mx-2 sm:mx-3 h-6 sm:h-8 w-px bg-gray-500/50"
        aria-hidden="true"
      />
      <img
        src="/billution-logo-reversed.svg"
        alt="vormals Billution"
        className="fb-legacy-dark h-5 sm:h-6 opacity-70"
      />
      <img
        src="/billution-logo.svg"
        alt=""
        aria-hidden="true"
        className="fb-legacy-light h-5 sm:h-6 opacity-70"
      />
    </div>
  );
}

/**
 * FaeBill brand icon mark — a rounded Navy square with 3 descending Mint/white lines.
 * Used as a compact logomark at the top of auth cards (Login, Register, etc.).
 *
 * @param className - Tailwind sizing classes, defaults to 'h-12 w-12'.
 */
export function FaeBillMark({ className = 'h-12 w-12' }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-2xl bg-[#0C1322] ${className}`}
      aria-hidden="true"
    >
      <svg width="58%" height="58%" viewBox="0 0 28 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="2" y1="3" x2="26" y2="3" stroke="#20D08A" strokeWidth="2.8" strokeLinecap="round" />
        <line x1="2" y1="11" x2="20" y2="11" stroke="rgba(255,255,255,0.55)" strokeWidth="2.2" strokeLinecap="round" />
        <line x1="2" y1="19" x2="13" y2="19" stroke="rgba(255,255,255,0.32)" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/**
 * Standalone legacy Billution brand mark.
 *
 * Renders the old Billution logo in a small, dimmed style, intended to be
 * placed inline next to the navigation bar during the rebranding transition.
 * Always in normal document flow — callers control visibility via Tailwind
 * responsive classes on their container.
 */
export function LegacyLogo({ className = 'h-5 sm:h-6' }: { className?: string }) {
  return (
    <img
      src="/billution-logo-reversed.svg"
      alt="vormals Billution"
      className={`${className} opacity-70`}
    />
  );
}

export default BrandLogo;
